import { MultiLanguageProperty, Property, SubmodelElementCollection } from 'api/v3/aas_core_meta/types';
import { Box, Typography } from '@mui/material';
import { StyledDataRow } from 'components/basics/StyledDataRow';
import { InfluxTimeSeriesDiagram } from './InfluxTimeSeriesDiagram';
import { useEffect, useState } from 'react';
import { TimeFrameSelection as TimeFrameSelection } from './TimeFrameSelection';
import { getTranslationText, hasSemanticId } from 'util/SubmodelResolverUtil';
import { IntlShape, useIntl } from 'react-intl';
import { SubmodelElementSemanticId } from 'enums/SubmodelElementSemanticId.enum';

export function InfluxTimeSeries(props: { submodelElement: SubmodelElementCollection }) {
    const intl = useIntl();

    const endpoint = (
        extractValueBySemanticId(
            props.submodelElement,
            SubmodelElementSemanticId.TimeSeriesLinkedSegmentEndpoint,
        ) as Property
    )?.value;

    const queryInAas = (
        extractValueBySemanticId(
            props.submodelElement,
            SubmodelElementSemanticId.TimeSeriesLinkedSegmentQuery,
        ) as Property
    )?.value;

    const name = extractIntlValueBySemanticId(
        props.submodelElement,
        SubmodelElementSemanticId.TimeSeriesSegmentName,
        intl,
    );
    const description = extractIntlValueBySemanticId(
        props.submodelElement,
        SubmodelElementSemanticId.TimeSeriesSegmentDescription,
        intl,
    );

    const [selectedTimeFrame, setSelectedTimeFrame] = useState('1d');
    const [query, setQuery] = useState(replaceTimeFrameInQuery(queryInAas, selectedTimeFrame));
    const showTimeSelection = window._env_.REACT_APP_LOCK_TIMESERIES_PERIOD_FEATURE_FLAG;

    useEffect(() => {
        setQuery(replaceTimeFrameInQuery(queryInAas, selectedTimeFrame));
    }, [queryInAas, selectedTimeFrame]);
    return endpoint && query ? (
        <Box sx={{ display: 'flex', flexDirection: 'column' }}>
            <StyledDataRow title={name}>
                <Box sx={{ marginTop: 1 }} />
                <Box sx={{ display: 'flex', justifyContent: 'left' }}>
                    <Typography
                        sx={{ color: 'primary.main', fontSize: 24, fontWeight: 600, lineHeight: 1 }}
                        component="span"
                    >
                        {description}
                    </Typography>
                </Box>
                {showTimeSelection && (
                    <Box sx={{ marginTop: 2 }}>
                        <TimeFrameSelection
                            selectedTimeFrame={selectedTimeFrame}
                            setSelectedTimeFrame={setSelectedTimeFrame}
                            selectableTimeFrames={['1m', '6h', '12h', '1d', '7d']}
                        />
                    </Box>
                )}
                <Box sx={{ marginTop: 2 }}>
                    <InfluxTimeSeriesDiagram endpoint={endpoint} query={query} />
                </Box>
            </StyledDataRow>
        </Box>
    ) : (
        <></>
    );
}

function extractValueBySemanticId(
    submodelElementCollection: SubmodelElementCollection,
    semanticId: SubmodelElementSemanticId,
) {
    return submodelElementCollection.value?.find((v) => hasSemanticId(v, semanticId));
}

function extractIntlValueBySemanticId(
    submodelElementCollection: SubmodelElementCollection,
    semanticId: SubmodelElementSemanticId,
    intl: IntlShape,
) {
    const multiLanguageProperty: MultiLanguageProperty | undefined = extractValueBySemanticId(
        submodelElementCollection,
        semanticId,
    ) as MultiLanguageProperty;
    return multiLanguageProperty ? getTranslationText(multiLanguageProperty, intl) : '';
}

const influxDbRangeParamRegEx = new RegExp(/range\([A-z]+: -?\d+[A-z]+\)/);

function replaceTimeFrameInQuery(query: string | undefined | null, timeFrame: string) {
    return query?.replace(influxDbRangeParamRegEx, `range(start: -${timeFrame})`);
}
